import React, { useState, useEffect, useRef, useCallback } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import { trainingService, employeeService, productService, productRevisionService, machineService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Plus, Filter, X, Pencil, Trash2 } from 'lucide-react';

const STATUS_OPTS = [
  { value: 'pianificata', label: 'Pianificata', color: '#3b82f6', cls: 'bg-blue-100 text-blue-700' },
  { value: 'svolta', label: 'Svolta', color: '#22c55e', cls: 'bg-green-100 text-green-700' },
  { value: 'annullata', label: 'Annullata', color: '#ef4444', cls: 'bg-red-100 text-red-700' },
];
const getStatusColor = (s) => STATUS_OPTS.find(o => o.value === s)?.color || '#6b7280';
const getStatusCls = (s) => STATUS_OPTS.find(o => o.value === s)?.cls || 'bg-slate-100 text-slate-600';
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('it-IT') : '-';

const EMPTY_FORM = {
  training_date: '', start_time: '', end_time: '', duration_hours: '',
  topic: '', trainer: '', employee_ids: [], product_id: '', revision_id: '',
  machine_id: '', status: 'pianificata', notes: ''
};

export default function CalendarioCorsiPage() {
  const { user } = useAuth();
  const calendarRef = useRef(null);
  const canEdit = ['admin', 'quality', 'management'].includes(user?.role);

  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  // Lookups
  const [employees, setEmployees] = useState([]);
  const [products, setProducts] = useState([]);
  const [revisions, setRevisions] = useState([]);
  const [machines, setMachines] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [departments, setDepartments] = useState([]);

  const [filters, setFilters] = useState({
    trainer: '', employee_id: '', department: '', product_id: '', machine_id: '', status: ''
  });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await trainingService.getAll(params);
      setSessions(res.data);
    } catch { toast.error('Errore caricamento sessioni'); } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    Promise.all([
      employeeService.getAll(),
      productService.getAll(),
      machineService.getAll(),
      trainingService.getTrainers().catch(() => ({ data: [] })),
      employeeService.getDepartments().catch(() => ({ data: [] })),
    ]).then(([e, p, m, t, d]) => {
      setEmployees(e.data);
      setProducts(p.data);
      setMachines(m.data);
      setTrainers(t.data);
      setDepartments(d.data);
    }).catch(() => {});
  }, []);

  const loadRevisions = async (pid) => {
    if (!pid) { setRevisions([]); return; }
    try { const r = await productRevisionService.getAll({ product_id: pid }); setRevisions(r.data); } catch { setRevisions([]); }
  };

  // Calendar events
  const calendarEvents = sessions.map(s => {
    let start = s.training_date;
    let end = s.training_date;
    if (s.start_time) start = `${s.training_date}T${s.start_time}`;
    if (s.end_time) end = `${s.training_date}T${s.end_time}`;
    else if (s.start_time && s.duration_hours) {
      const [h, m] = s.start_time.split(':').map(Number);
      const endMin = h * 60 + m + (s.duration_hours * 60);
      const eh = String(Math.floor(endMin / 60)).padStart(2, '0');
      const em = String(Math.floor(endMin % 60)).padStart(2, '0');
      end = `${s.training_date}T${eh}:${em}`;
    }
    return {
      id: s.id,
      title: `${s.topic} (${s.trainer})`,
      start,
      end: end !== s.training_date ? end : undefined,
      allDay: !s.start_time,
      backgroundColor: getStatusColor(s.status),
      borderColor: getStatusColor(s.status),
      extendedProps: s
    };
  });

  const handleEventClick = (info) => setShowDetail(info.event.extendedProps);
  const handleDateSelect = (info) => {
    if (!canEdit) return;
    setEditing(null);
    setForm({ ...EMPTY_FORM, training_date: info.startStr.slice(0, 10), start_time: info.startStr.includes('T') ? info.startStr.slice(11, 16) : '' });
    setShowModal(true);
  };

  const openCreate = () => { setEditing(null); setForm(EMPTY_FORM); setShowModal(true); };
  const openEdit = (s) => {
    setEditing(s);
    setForm({
      training_date: s.training_date || '', start_time: s.start_time || '', end_time: s.end_time || '',
      duration_hours: s.duration_hours || '', topic: s.topic || '', trainer: s.trainer || '',
      employee_ids: s.employee_ids || [], product_id: s.product_id || '', revision_id: s.revision_id || '',
      machine_id: s.machine_id || '', status: s.status || 'pianificata', notes: s.notes || ''
    });
    if (s.product_id) loadRevisions(s.product_id);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.training_date || !form.topic || !form.trainer) { toast.error('Data, tema e formatore obbligatori'); return; }
    if (!form.duration_hours || parseFloat(form.duration_hours) <= 0) { toast.error('Durata ore obbligatoria'); return; }
    try {
      const payload = { ...form, duration_hours: parseFloat(form.duration_hours) };
      if (!payload.start_time) payload.start_time = null;
      if (!payload.end_time) payload.end_time = null;
      if (!payload.product_id) payload.product_id = null;
      if (!payload.revision_id) payload.revision_id = null;
      if (!payload.machine_id) payload.machine_id = null;
      if (!payload.notes) payload.notes = null;
      if (editing) { await trainingService.update(editing.id, payload); toast.success('Sessione aggiornata'); }
      else { await trainingService.create(payload); toast.success('Sessione creata'); }
      setShowModal(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questa sessione?')) return;
    try { await trainingService.delete(id); toast.success('Sessione eliminata'); setShowDetail(null); load(); } catch { toast.error('Errore eliminazione'); }
  };

  // Auto-calc duration from start/end times
  const calcDuration = (start, end) => {
    if (!start || !end) return '';
    const [sh, sm] = start.split(':').map(Number);
    const [eh, em] = end.split(':').map(Number);
    const diff = (eh * 60 + em - sh * 60 - sm) / 60;
    return diff > 0 ? diff.toFixed(1) : '';
  };

  const toggleEmployee = (empId) => {
    setForm(f => ({
      ...f,
      employee_ids: f.employee_ids.includes(empId)
        ? f.employee_ids.filter(id => id !== empId)
        : [...f.employee_ids, empId]
    }));
  };

  const hasFilters = Object.values(filters).some(v => v);

  return (
    <div className="space-y-4" data-testid="calendario-corsi-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-slate-900" data-testid="calendario-corsi-title">Calendario Corsi</h1>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)} data-testid="training-filters-btn"
            className={hasFilters ? 'border-blue-300 bg-blue-50 text-blue-700' : ''}>
            <Filter className="w-4 h-4 mr-1" />Filtri
          </Button>
          {canEdit && <Button size="sm" onClick={openCreate} data-testid="new-session-btn"><Plus className="w-4 h-4 mr-1" />Nuova sessione</Button>}
        </div>
      </div>

      {showFilters && (
        <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="training-filters-panel">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Formatore</label>
              <select value={filters.trainer} onChange={e => setFilters(f => ({ ...f, trainer: e.target.value }))} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" data-testid="filter-trainer">
                <option value="">Tutti</option>
                {trainers.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Dipendente</label>
              <select value={filters.employee_id} onChange={e => setFilters(f => ({ ...f, employee_id: e.target.value }))} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" data-testid="filter-employee">
                <option value="">Tutti</option>
                {employees.map(emp => <option key={emp.id} value={emp.id}>{emp.last_name} {emp.first_name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Reparto</label>
              <select value={filters.department} onChange={e => setFilters(f => ({ ...f, department: e.target.value }))} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" data-testid="filter-department">
                <option value="">Tutti</option>
                {departments.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Prodotto</label>
              <select value={filters.product_id} onChange={e => setFilters(f => ({ ...f, product_id: e.target.value }))} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" data-testid="filter-product">
                <option value="">Tutti</option>
                {products.map(p => <option key={p.id} value={p.id}>{p.code} - {p.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Macchina</label>
              <select value={filters.machine_id} onChange={e => setFilters(f => ({ ...f, machine_id: e.target.value }))} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" data-testid="filter-machine">
                <option value="">Tutte</option>
                {machines.map(m => <option key={m.id} value={m.id}>{m.machine_code} - {m.machine_name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Stato</label>
              <select value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" data-testid="filter-status">
                <option value="">Tutti</option>
                {STATUS_OPTS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
          </div>
          {hasFilters && <div className="mt-3 flex justify-end"><button onClick={() => setFilters({ trainer: '', employee_id: '', department: '', product_id: '', machine_id: '', status: '' })} className="text-sm text-blue-600 hover:text-blue-800 font-medium">Resetta filtri</button></div>}
        </div>
      )}

      {/* Calendar */}
      <div className="bg-white border border-slate-200 rounded-xl p-3 sm:p-4 training-calendar" data-testid="training-calendar-view">
        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
          initialView="dayGridMonth"
          headerToolbar={{ left: 'prev,next today', center: 'title', right: 'dayGridMonth,timeGridWeek,timeGridDay,listWeek' }}
          buttonText={{ today: 'Oggi', month: 'Mese', week: 'Settimana', day: 'Giorno', list: 'Elenco' }}
          locale="it" firstDay={1} allDaySlot={true} allDayText="Tutto il giorno"
          slotMinTime="06:00:00" slotMaxTime="22:00:00" slotDuration="00:30:00"
          height="auto" contentHeight={600} events={calendarEvents}
          selectable={canEdit} selectMirror={true}
          select={handleDateSelect} eventClick={handleEventClick}
          nowIndicator={true}
          businessHours={{ daysOfWeek: [1, 2, 3, 4, 5], startTime: '07:00', endTime: '19:00' }}
          eventDisplay="block"
        />
      </div>

      {/* Detail panel */}
      {showDetail && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/40 p-4" onClick={() => setShowDetail(null)}>
          <div className="flex min-h-full items-center justify-center">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg" onClick={e => e.stopPropagation()} data-testid="session-detail-panel">
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
                <h2 className="text-lg font-bold text-slate-900">Dettaglio sessione</h2>
                <div className="flex items-center gap-1">
                  {canEdit && <button onClick={() => { openEdit(showDetail); setShowDetail(null); }} className="p-1.5 hover:bg-slate-100 rounded-lg"><Pencil className="w-4 h-4 text-slate-500" /></button>}
                  {canEdit && <button onClick={() => handleDelete(showDetail.id)} className="p-1.5 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4 text-red-500" /></button>}
                  <button onClick={() => setShowDetail(null)} className="p-1.5 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5 text-slate-500" /></button>
                </div>
              </div>
              <div className="p-6 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <DetailField label="Tema" value={showDetail.topic} />
                  <DetailField label="Formatore" value={showDetail.trainer} />
                  <DetailField label="Data" value={fmtDate(showDetail.training_date)} />
                  <DetailField label="Durata" value={`${showDetail.duration_hours} ore`} />
                  <DetailField label="Ora inizio" value={showDetail.start_time || '-'} />
                  <DetailField label="Ora fine" value={showDetail.end_time || '-'} />
                  <DetailField label="Stato" value={<span className={`text-xs font-medium px-2 py-0.5 rounded-full ${getStatusCls(showDetail.status)}`}>{STATUS_OPTS.find(o => o.value === showDetail.status)?.label}</span>} />
                  {showDetail.product_code && <DetailField label="Prodotto" value={`${showDetail.product_code} - ${showDetail.product_name || ''}`} />}
                  {showDetail.revision_code && <DetailField label="Revisione" value={showDetail.revision_code} />}
                  {showDetail.machine_code && <DetailField label="Macchina" value={`${showDetail.machine_code} - ${showDetail.machine_name || ''}`} />}
                </div>
                {showDetail.notes && <DetailField label="Note" value={showDetail.notes} />}
                <div>
                  <p className="text-xs font-medium text-slate-500 mb-1">Dipendenti coinvolti ({(showDetail.employees || showDetail.employee_ids || []).length})</p>
                  <div className="flex flex-wrap gap-1">
                    {(showDetail.employees || []).map(e => (
                      <span key={e.id} className="text-xs bg-slate-100 text-slate-700 px-2 py-0.5 rounded-full">{e.last_name || ''} {e.first_name || ''}</span>
                    ))}
                    {!showDetail.employees && (showDetail.employee_ids || []).map(id => (
                      <span key={id} className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{employees.find(e => e.id === id)?.last_name || id}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="session-modal">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl">
              <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
                <h2 className="text-lg font-bold text-slate-900">{editing ? 'Modifica sessione' : 'Nuova sessione formativa'}</h2>
                <button onClick={() => setShowModal(false)} className="p-1.5 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5 text-slate-500" /></button>
              </div>
              <form onSubmit={handleSubmit} className="p-5 space-y-3">
                <div className="grid grid-cols-4 gap-3">
                  <div className="col-span-2"><label className="block text-xs font-medium text-slate-700 mb-1">Tema formazione *</label>
                    <input type="text" value={form.topic} onChange={e => setForm(f => ({ ...f, topic: e.target.value }))} className="w-full px-3 py-1.5 text-sm border border-slate-200 rounded-lg" required data-testid="form-topic" /></div>
                  <div><label className="block text-xs font-medium text-slate-700 mb-1">Formatore *</label>
                    <input type="text" value={form.trainer} onChange={e => setForm(f => ({ ...f, trainer: e.target.value }))} list="trainer-list" className="w-full px-3 py-1.5 text-sm border border-slate-200 rounded-lg" required data-testid="form-trainer" />
                    <datalist id="trainer-list">{trainers.map(t => <option key={t} value={t} />)}</datalist></div>
                  <div><label className="block text-xs font-medium text-slate-700 mb-1">Stato</label>
                    <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} className="w-full px-3 py-1.5 text-sm border border-slate-200 rounded-lg" data-testid="form-status">
                      {STATUS_OPTS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select></div>
                </div>
                <div className="grid grid-cols-4 gap-3">
                  <div><label className="block text-xs font-medium text-slate-700 mb-1">Data formazione *</label>
                    <input type="date" value={form.training_date} onChange={e => setForm(f => ({ ...f, training_date: e.target.value }))} className="w-full px-3 py-1.5 text-sm border border-slate-200 rounded-lg" required data-testid="form-date" /></div>
                  <div><label className="block text-xs font-medium text-slate-700 mb-1">Ora inizio</label>
                    <input type="time" value={form.start_time} onChange={e => { const v = e.target.value; setForm(f => { const dur = calcDuration(v, f.end_time); return { ...f, start_time: v, ...(dur ? { duration_hours: dur } : {}) }; }); }} className="w-full px-3 py-1.5 text-sm border border-slate-200 rounded-lg" data-testid="form-start-time" /></div>
                  <div><label className="block text-xs font-medium text-slate-700 mb-1">Ora fine</label>
                    <input type="time" value={form.end_time} onChange={e => { const v = e.target.value; setForm(f => { const dur = calcDuration(f.start_time, v); return { ...f, end_time: v, ...(dur ? { duration_hours: dur } : {}) }; }); }} className="w-full px-3 py-1.5 text-sm border border-slate-200 rounded-lg" data-testid="form-end-time" /></div>
                  <div><label className="block text-xs font-medium text-slate-700 mb-1">Durata ore *</label>
                    <input type="number" step="0.5" min="0.5" value={form.duration_hours} onChange={e => setForm(f => ({ ...f, duration_hours: e.target.value }))} className="w-full px-3 py-1.5 text-sm border border-slate-200 rounded-lg" required data-testid="form-duration" /></div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div><label className="block text-xs font-medium text-slate-700 mb-1">Prodotto</label>
                    <select value={form.product_id} onChange={e => { setForm(f => ({ ...f, product_id: e.target.value, revision_id: '' })); loadRevisions(e.target.value); }} className="w-full px-3 py-1.5 text-sm border border-slate-200 rounded-lg" data-testid="form-product">
                      <option value="">Nessuno</option>
                      {products.map(p => <option key={p.id} value={p.id}>{p.code} - {p.name}</option>)}
                    </select></div>
                  <div><label className="block text-xs font-medium text-slate-700 mb-1">Revisione</label>
                    <select value={form.revision_id} onChange={e => setForm(f => ({ ...f, revision_id: e.target.value }))} className="w-full px-3 py-1.5 text-sm border border-slate-200 rounded-lg" disabled={!form.product_id} data-testid="form-revision">
                      <option value="">Nessuna</option>
                      {revisions.map(r => <option key={r.id} value={r.id}>{r.revision_code}</option>)}
                    </select></div>
                  <div><label className="block text-xs font-medium text-slate-700 mb-1">Macchina</label>
                    <select value={form.machine_id} onChange={e => setForm(f => ({ ...f, machine_id: e.target.value }))} className="w-full px-3 py-1.5 text-sm border border-slate-200 rounded-lg" data-testid="form-machine">
                      <option value="">Nessuna</option>
                      {machines.map(m => <option key={m.id} value={m.id}>{m.machine_code} - {m.machine_name}</option>)}
                    </select></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">Dipendenti coinvolti ({form.employee_ids.length})</label>
                    <div className="border border-slate-200 rounded-lg max-h-28 overflow-y-auto p-1.5 space-y-0.5" data-testid="form-employees">
                      {employees.map(emp => (
                        <label key={emp.id} className="flex items-center gap-2 px-2 py-0.5 rounded hover:bg-slate-50 cursor-pointer text-sm">
                          <input type="checkbox" checked={form.employee_ids.includes(emp.id)} onChange={() => toggleEmployee(emp.id)} className="rounded border-slate-300" />
                          <span className="font-mono text-xs text-slate-500">{emp.employee_code}</span>
                          <span className="truncate">{emp.last_name} {emp.first_name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div><label className="block text-xs font-medium text-slate-700 mb-1">Note</label>
                    <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={4} className="w-full px-3 py-1.5 text-sm border border-slate-200 rounded-lg" data-testid="form-notes" /></div>
                </div>
                <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
                  <Button type="button" variant="outline" size="sm" onClick={() => setShowModal(false)}>Annulla</Button>
                  <Button type="submit" size="sm" data-testid="session-save-btn">{editing ? 'Salva' : 'Crea sessione'}</Button>
                </div>
              </form>
            </div>
        </div>
      )}
    </div>
  );
}

function DetailField({ label, value }) {
  return (
    <div>
      <p className="text-xs font-medium text-slate-500">{label}</p>
      <p className="text-sm text-slate-900 mt-0.5">{value || '-'}</p>
    </div>
  );
}
